/**
 * Wave 3 · 24 #10 — "Collections forecast": how much money is expected each month for the next 6 (or 12) months,
 * from the unpaid invoices (due date, else submitted + the client's usual days to pay), the milestones that are
 * ready to bill today and the planned ones (GET /invoicing/collection-forecast). Stacked columns, one per month;
 * hover a month for its split, click it for the invoices behind it (the table view).
 * The page's project / client / system / milestone filters are sent to the API (`scopeLabel` names them in the head);
 * an overdue invoice says since when ("overdue since 4 Mar"), not "overdue today".
 */
import { useEffect, useMemo, useState } from 'react';
import { CalendarRange, CloudOff, Info } from 'lucide-react';
import { formatDate, formatMoney, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { CardFoot, CardHead, Segmented, SfCard, plural } from './SfUi';
import { COLLECTION_SERIES, basisText, collectionBars, compactAed, seriesTotals } from './collectionsModel';

const aed0 = (value) => formatMoney(value, { decimals: 0 });
const SERIES_LABEL = Object.fromEntries(COLLECTION_SERIES.map((series) => [series.key, series.label]));
const SERIES_CLASS = Object.fromEntries(COLLECTION_SERIES.map((series) => [series.key, series.className]));
const SERIES_SHORT = { overdue: 'overdue', raised: 'due', ready: 'ready to bill', planned: 'planned' };

function Tooltip({ bar }) {
  return (
    <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 w-[220px] -translate-x-1/2 rounded-xl border border-line bg-surface p-3 text-left text-[12px] text-ink shadow-overlay" role="tooltip">
      <p className="font-heading text-[13px] font-bold">{bar.label} · {aed0(bar.total)}</p>
      <ul className="mt-1.5 space-y-1">
        {bar.segments.map((segment) => (
          <li key={segment.key} className="flex items-center gap-2">
            <span className={cn('h-2.5 w-2.5 shrink-0 rounded-sm', segment.className)} aria-hidden="true" />
            <span className="min-w-0 flex-1 text-ink-2">{segment.label}</span>
            <span className="tabular-nums">{aed0(segment.value)}</span>
          </li>
        ))}
      </ul>
      <p className="mt-1.5 text-[11.5px] text-ink-3">{plural(bar.count, 'invoice')} · click for the list</p>
    </div>
  );
}

/** "overdue since 4 Mar" / "due 12 Nov" / "ready to bill 2 Dec" for one invoice in the month detail. */
export function itemWhen(item) {
  if (item.series === 'overdue') {
    const since = item.due_on || '';
    return since ? `overdue since ${formatDate(since, { year: 'auto' })}` : 'overdue';
  }
  return `${SERIES_SHORT[item.series] || item.series} ${formatDate(item.expected, { year: 'never' })}`;
}

/**
 * props: data (GET /invoicing/collection-forecast), loading, error, onRetry, months + onMonthsChange,
 * scopeLabel ("all projects", or the filters the API applied: "Abu Dhabi Police - Al Dar · Handing Over"),
 * scopeNote (filters the forecast does not follow, e.g. "search and status not applied").
 */
export function CollectionsForecastCard({ data, loading, error, onRetry, months = 6, onMonthsChange, scopeLabel = '', scopeNote = '' }) {
  const bars = useMemo(() => collectionBars(data?.months), [data]);
  const totals = useMemo(() => seriesTotals(data?.months), [data]);
  const [hover, setHover] = useState(null);
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    // Opens on the first month with money in it.
    setSelected((prev) => (prev && bars.some((bar) => bar.key === prev) ? prev : (bars.find((bar) => bar.total > 0) || {}).key || null));
  }, [bars]);
  const selectedBar = bars.find((bar) => bar.key === selected);

  if (error) {
    return (
      <SfCard data-testid="collections-forecast">
        <CardHead icon={CalendarRange} title="Collections forecast" />
        <EmptyState layout="inline" tone="red" icon={CloudOff} title="Couldn't load the collections forecast" text={error}
          action={onRetry ? <Button variant="outline" size="sm" onClick={onRetry}>Try again</Button> : null} />
      </SfCard>
    );
  }
  if (!data) return loading ? <div className="ui-skeleton-shimmer h-[300px] rounded-card" aria-busy="true" /> : null;

  return (
    <SfCard data-testid="collections-forecast" className={loading ? 'opacity-70 transition-opacity' : undefined}>
      <CardHead
        icon={CalendarRange}
        title="Collections forecast"
        sub={(
          <span data-testid="collections-scope">
            Next {bars.length} months · {scopeLabel || 'all projects'}
            {scopeNote ? <span className="text-ink-4"> · {scopeNote}</span> : null}
          </span>
        )}
        right={onMonthsChange ? (
          <Segmented size="sm" ariaLabel="Months ahead" value={String(months)} onChange={(value) => onMonthsChange(Number(value))}
            options={[{ key: '6', label: '6 months' }, { key: '12', label: '12 months' }]} testId="collections-months" />
        ) : null}
      />
      <div className="grid grid-cols-1 gap-x-6 gap-y-4 px-4 pb-4 pt-3.5 sm:px-[18px] lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="font-heading text-[24px] font-extrabold tabular-nums text-ink">
              <small className="mr-1 font-sans text-[12px] font-bold text-ink-3">AED</small>{formatNumber(data.total, { decimals: 0 })}
            </span>
            <span className="text-[12.5px] text-ink-3">expected in {bars.length} months{totals.overdue > 0 ? <> · <b className="font-semibold text-status-red-ink">{aed0(totals.overdue)} overdue now</b></> : null}</span>
          </div>
          <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-ink-2" aria-label="Legend" data-testid="collections-legend">
            {COLLECTION_SERIES.map((series) => (
              <li key={series.key} className="inline-flex items-center gap-1.5">
                <span className={cn('h-2.5 w-2.5 rounded-sm', series.className)} aria-hidden="true" />
                {series.label}<span className="tabular-nums text-ink-3">{compactAed(totals[series.key])}</span>
              </li>
            ))}
          </ul>
          <div className="relative mt-4 h-[200px] border-b border-line" role="list" aria-label="Expected collections per month">
            <div className="pointer-events-none absolute inset-x-0 top-0 border-t border-dashed border-line-2" aria-hidden="true" />
            <div className="pointer-events-none absolute inset-x-0 top-1/2 border-t border-dashed border-line-2" aria-hidden="true" />
            <div className="absolute inset-0 flex items-end justify-around gap-2 px-1">
              {bars.map((bar) => (
                <div key={bar.key} role="listitem" className="flex h-full min-w-0 flex-1 flex-col items-center justify-end">
                  <div className="relative flex w-full flex-col items-center" style={{ height: `calc(${Math.max(bar.total > 0 ? 3 : 0, bar.heightPct * 0.82)}% + 20px)` }}>
                    {hover === bar.key && bar.total > 0 ? <Tooltip bar={bar} /> : null}
                    <span className={cn('mb-1 h-4 whitespace-nowrap text-[11.5px] font-semibold leading-4 tabular-nums', bar.total > 0 ? 'text-ink-2' : 'text-ink-4')}>{bar.total > 0 ? compactAed(bar.total) : '—'}</span>
                    <button
                      type="button"
                      onMouseEnter={() => setHover(bar.key)}
                      onMouseLeave={() => setHover(null)}
                      onFocus={() => setHover(bar.key)}
                      onBlur={() => setHover(null)}
                      onClick={() => setSelected(bar.key)}
                      aria-pressed={selected === bar.key}
                      aria-label={`${bar.label}: ${aed0(bar.total)}${bar.segments.length ? ` — ${bar.segments.map((segment) => `${segment.label} ${aed0(segment.value)}`).join(', ')}` : ''}`}
                      className={cn(
                        'flex w-full max-w-[56px] flex-1 flex-col-reverse gap-[2px] overflow-hidden rounded-t-[4px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        selected === bar.key && 'outline outline-2 outline-offset-2 outline-[color:var(--brand)]'
                      )}
                      data-testid={`collections-month-${bar.key}`}
                    >
                      {bar.segments.map((segment) => (
                        <span key={segment.key} className={cn('w-full', segment.className)} style={{ flexGrow: segment.value, flexBasis: 0, minHeight: 2 }} />
                      ))}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-1.5 flex justify-around gap-2 px-1 text-[11.5px] text-ink-3">
            {bars.map((bar) => <span key={bar.key} className={cn('min-w-0 flex-1 text-center', selected === bar.key && 'font-semibold text-ink')}>{bar.label.replace(/ \d{4}$/, '')}</span>)}
          </div>
        </div>
        <div className="min-w-0 rounded-xl border border-line bg-surface-2 p-3" data-testid="collections-month-detail">
          {selectedBar ? (
            <>
              <p className="font-heading text-[13.5px] font-bold text-ink">{selectedBar.label} · {aed0(selectedBar.total)}</p>
              <p className="text-[12px] text-ink-3">{plural(selectedBar.count, 'invoice')}{selectedBar.count > selectedBar.items.length ? ` · biggest ${selectedBar.items.length}` : ''}</p>
              <ul className="mt-2 space-y-1.5">
                {selectedBar.items.map((item) => (
                  <li key={`${item.kind}-${item.invoice_id}`} className="grid grid-cols-[10px_minmax(0,1fr)_auto] items-start gap-x-2 text-[12px]">
                    <span className={cn('mt-1 h-2.5 w-2.5 rounded-sm', SERIES_CLASS[item.series])} title={SERIES_LABEL[item.series]} aria-hidden="true" />
                    <span className="min-w-0">
                      <span className="block truncate font-semibold text-ink">{item.project_name || 'Project'}</span>
                      <span className="block text-ink-3">
                        {item.milestone || '—'}{item.reference ? ` · ${item.reference}` : ''}
                        {' · '}
                        <span className={cn('whitespace-nowrap', item.series === 'overdue' && 'font-semibold text-status-red-ink')} data-testid={`collections-item-when-${item.invoice_id}`}>{itemWhen(item)}</span>
                      </span>
                    </span>
                    <span className="whitespace-nowrap tabular-nums text-ink">{aed0(item.amount)}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : <p className="text-[12.5px] text-ink-3">Nothing expected in the next {bars.length} months.</p>}
        </div>
      </div>
      <CardFoot className="text-[12px]">
        <span className="flex items-start gap-1.5"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />{basisText(data.basis)}</span>
        <span className="tabular-nums">
          {data.later?.amount ? <>Later: {aed0(data.later.amount)} · </> : null}
          Not scheduled: <b className="font-semibold text-ink-2">{aed0(data.unscheduled?.amount || 0)}</b> ({plural(data.unscheduled?.count || 0, 'milestone')} without a planned date)
        </span>
      </CardFoot>
    </SfCard>
  );
}
